import { useMemo } from 'react';
import type { EntityName, EntitySecurity, SecurityAction } from '../../auth/Security';
import { SECURITY_ACTIONS, useSecurity } from '../../auth/Security';
import type { DataRowSecurityActionPermissions } from '../types';

const DATA_ROW_SECURITY_ACTIONS: SecurityAction[] = [
    SECURITY_ACTIONS.READ,
    SECURITY_ACTIONS.EDIT,
    SECURITY_ACTIONS.DELETE,
    SECURITY_ACTIONS.SHARE,
    SECURITY_ACTIONS.ACTIVATE,
    SECURITY_ACTIONS.INACTIVATE,
    SECURITY_ACTIONS.COMMENT
];

const EMPTY_SECURITY_ACTIONS: SecurityAction[] = [];

const EMPTY_DATA_ROW_SECURITY_ACTION_PERMISSIONS: DataRowSecurityActionPermissions = {
    [SECURITY_ACTIONS.READ]: false,
    [SECURITY_ACTIONS.EDIT]: false,
    [SECURITY_ACTIONS.DELETE]: false,
    [SECURITY_ACTIONS.SHARE]: false,
    [SECURITY_ACTIONS.ACTIVATE]: false,
    [SECURITY_ACTIONS.INACTIVATE]: false,
    [SECURITY_ACTIONS.COMMENT]: false
};

export function useDataRowPermissions(
    entityName: EntityName | undefined,
    entitySecurity?: EntitySecurity
): DataRowSecurityActionPermissions {
    const { allowed: securityDecisions } = useSecurity(
        entityName ? DATA_ROW_SECURITY_ACTIONS : EMPTY_SECURITY_ACTIONS,
        entityName ? { entityName, entitySecurity: entitySecurity as EntitySecurity } : undefined
    );

    return useMemo(() => {
        if (!entityName) {
            return EMPTY_DATA_ROW_SECURITY_ACTION_PERMISSIONS;
        }

        const [canRead, canEdit, canDelete, canShare, canActivate, canInactivate, canComment] = securityDecisions;

        return {
            [SECURITY_ACTIONS.READ]: canRead,
            [SECURITY_ACTIONS.EDIT]: canEdit,
            [SECURITY_ACTIONS.DELETE]: canDelete,
            [SECURITY_ACTIONS.SHARE]: canShare,
            [SECURITY_ACTIONS.ACTIVATE]: canActivate,
            [SECURITY_ACTIONS.INACTIVATE]: canInactivate,
            [SECURITY_ACTIONS.COMMENT]: canComment
        };
    }, [entityName, securityDecisions]);
}
